import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../../analytics';
import { FeedCard } from '../../components/FeedCard/FeedCard';
import { useData } from '../../context/DataContext';
import styles from './Feed.module.css';

export function Feed() {
  const { spots, loading, loadingMoreSpots, hasMoreSpots, loadMoreSpots, error } = useData();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMoreSpots || loading || loadingMoreSpots || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadMoreSpots();
        }
      },
      { rootMargin: '320px 0px' }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMoreSpots, loadMoreSpots, loading, loadingMoreSpots]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <p className={styles.kicker}>Full community stream</p>
          
          <p className={styles.subtitle}>Every recent car sighting from the community, newest first.</p>
        </div>
        <div className={styles.actions}>
          <Link
            to="/search"
            className={styles.secondaryAction}
            onClick={() => trackEvent('cta_click', { context: 'feed_page', label: 'search_spots' })}
          >
            Search spots
          </Link>
          <Link
            to="/spot"
            className={styles.primaryAction}
            onClick={() => trackEvent('cta_click', { context: 'feed_page', label: 'post_spot' })}
          >
            Post spot
          </Link>
        </div>
      </div>

      {error && <p className={styles.error}>{error}</p>}
      {loading ? (
        <p className={styles.loading}>Loading feed…</p>
      ) : spots.length === 0 ? (
        <div className={styles.empty}>
          <p>No spots yet. Be the first to share a car.</p>
          <Link
            to="/spot"
            className={styles.emptyCta}
            onClick={() => trackEvent('cta_click', { context: 'feed_page_empty', label: 'post_spot' })}
          >
            Spot a car
          </Link>
        </div>
      ) : (
        <div className={styles.feed}>
          {spots.map((spot) => (
            <FeedCard key={spot.id} spot={spot} compact />
          ))}
          <div ref={loadMoreRef} />
          {loadingMoreSpots && <p className={styles.loading}>Loading more spots…</p>}
          {!loadingMoreSpots && hasMoreSpots && (
            <button type="button" className={styles.secondaryAction} onClick={() => void loadMoreSpots()}>
              Load more spots
            </button>
          )}
        </div>
      )}
    </div>
  );
}
